/**
 * Back in stock (Shopify Forms): when "Replace with notify when sold out" is on,
 * shows a trigger where Add to cart would be; the Forms app block opens in a
 * native <dialog> modal with backdrop, Escape to close, and scroll lock.
 *
 * Syncs Shopify Forms custom fields (title/product = product name, colour, size, plus
 * variant/url when present) by data-testid field-custom#<handle>, searching
 * nested open shadow roots under shopify-forms-embed.
 *
 * Hiding autofilled rows is opt-in: set window.productBackInStockInline.hideAutofilledCustomFields
 * to true when you are done testing (default false = fields stay visible).
 */
(function () {
  const WRAPPER_SELECTOR = '.product-form__primary-cta-wrapper[data-replace-with-notify-when-sold-out="true"]';
  const TRIGGER_CLASS = 'product-back-in-stock-inline__trigger';
  const FORM_HIDDEN_CLASS = 'is-back-in-stock-form-hidden';
  const FORM_WRAP_SELECTOR = '.product__app-block--back-in-stock-reveal';
  const DIALOG_CLASS = 'product-back-in-stock-modal-dialog';
  const CUSTOM_FIELD_KEYS = ['title', 'product', 'color', 'colour', 'size', 'variant', 'url'];

  let modalRestore = null;

  function cfg() {
    return window.productBackInStockInline || {};
  }

  function localeDefaults() {
    const lang = ((document.documentElement && document.documentElement.lang) || '').toLowerCase();
    if (lang.indexOf('ja') === 0) {
      return {
        triggerLabel: '再入荷通知を受け取る',
        closeLabel: '閉じる',
        modalTitle: '再入荷通知を受け取る'
      };
    }
    return {
      triggerLabel: 'Notify when available',
      closeLabel: 'Close',
      modalTitle: 'Notify when back in stock'
    };
  }

  function triggerLabel() {
    return cfg().triggerLabel || localeDefaults().triggerLabel;
  }

  function closeLabel() {
    return cfg().closeLabel || localeDefaults().closeLabel;
  }

  function modalTitleText() {
    return cfg().modalTitle || localeDefaults().modalTitle;
  }

  function sectionRootFromWrapper(wrapper) {
    return wrapper.closest('[id^="shopify-section-"]');
  }

  function formWrapInSection(section) {
    if (!section) return null;
    let wrap = section.querySelector(FORM_WRAP_SELECTOR);
    if (wrap) return wrap;
    const dialog = document.querySelector('dialog.' + DIALOG_CLASS);
    if (dialog && dialog.open && dialog.dataset.sectionId === section.id) {
      return dialog.querySelector(FORM_WRAP_SELECTOR);
    }
    return null;
  }

  function hideFormWrap(wrap) {
    if (!wrap) return;
    const dialog = wrap.closest('dialog.' + DIALOG_CLASS);
    if (dialog && dialog.open) {
      dialog.close();
      return;
    }
    wrap.classList.add(FORM_HIDDEN_CLASS);
  }

  function onDialogClose() {
    const dialog = document.querySelector('dialog.' + DIALOG_CLASS);
    const wrap = dialog && dialog.querySelector(FORM_WRAP_SELECTOR);
    document.body.style.overflow = '';

    if (wrap && modalRestore && modalRestore.parent) {
      wrap.classList.add(FORM_HIDDEN_CLASS);
      const parent = modalRestore.parent;
      const nextSibling = modalRestore.nextSibling;
      try {
        if (parent.isConnected) {
          if (nextSibling && nextSibling.parentNode === parent) {
            parent.insertBefore(wrap, nextSibling);
          } else {
            parent.appendChild(wrap);
          }
        }
      } catch (e) {
        console.warn('[Back in stock] Could not restore form to section', e);
      }
    }

    if (dialog) {
      delete dialog.dataset.sectionId;
    }
    modalRestore = null;
  }

  function isPointerInsideModalCard(event, innerEl) {
    if (!innerEl) return false;
    if (typeof event.composedPath === 'function') {
      return event.composedPath().some(function (node) {
        return node === innerEl || (node instanceof Node && innerEl.contains(node));
      });
    }
    return !!(event.target && innerEl.contains(event.target));
  }

  function attachDialogDismissOnOverlay(d) {
    if (d.hasAttribute('data-back-in-stock-overlay-dismiss')) return;
    d.setAttribute('data-back-in-stock-overlay-dismiss', '');
    d.addEventListener(
      'pointerdown',
      function (e) {
        if (!d.open) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const innerEl = d.querySelector('.' + DIALOG_CLASS + '__inner');
        if (!isPointerInsideModalCard(e, innerEl)) {
          d.close();
        }
      },
      true
    );
  }

  function getOrCreateDialog() {
    let d = document.querySelector('dialog.' + DIALOG_CLASS);
    if (d && !d.querySelector('.' + DIALOG_CLASS + '__backdrop-hit')) {
      d.remove();
      d = null;
    }
    if (d) {
      attachDialogDismissOnOverlay(d);
      return d;
    }

    d = document.createElement('dialog');
    d.className = DIALOG_CLASS;
    d.setAttribute('aria-modal', 'true');
    d.setAttribute('aria-labelledby', 'product-back-in-stock-modal-title');

    const hit = document.createElement('div');
    hit.className = DIALOG_CLASS + '__backdrop-hit';
    hit.setAttribute('aria-hidden', 'true');
    hit.addEventListener('click', function () {
      d.close();
    });

    const inner = document.createElement('div');
    inner.className = DIALOG_CLASS + '__inner';

    const header = document.createElement('div');
    header.className = DIALOG_CLASS + '__header';

    const title = document.createElement('h2');
    title.id = 'product-back-in-stock-modal-title';
    title.className = 'visually-hidden';
    title.textContent = modalTitleText();

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = DIALOG_CLASS + '__close button button--text';
    closeBtn.setAttribute('aria-label', closeLabel());
    closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      d.close();
    });

    const body = document.createElement('div');
    body.className = DIALOG_CLASS + '__body';

    header.appendChild(closeBtn);
    body.appendChild(title);
    inner.appendChild(header);
    inner.appendChild(body);
    d.appendChild(hit);
    d.appendChild(inner);
    d.addEventListener('close', onDialogClose);
    attachDialogDismissOnOverlay(d);
    document.body.appendChild(d);
    return d;
  }

  function openBackInStockModal(wrap, section) {
    const dialog = getOrCreateDialog();
    if (dialog.open && dialog.dataset.sectionId === section.id) {
      queueSyncShopifyFormsCustomFields(section);
      return;
    }
    if (dialog.open) {
      dialog.close();
    }

    const body = dialog.querySelector('.' + DIALOG_CLASS + '__body');
    const closeBtn = dialog.querySelector('.' + DIALOG_CLASS + '__close');
    const title = dialog.querySelector('#product-back-in-stock-modal-title');
    if (title) title.textContent = modalTitleText();
    if (closeBtn) closeBtn.setAttribute('aria-label', closeLabel());

    modalRestore = { parent: wrap.parentNode, nextSibling: wrap.nextSibling };
    dialog.dataset.sectionId = section.id;

    body.appendChild(wrap);
    wrap.classList.remove(FORM_HIDDEN_CLASS);

    try {
      dialog.showModal();
    } catch (e) {
      console.error('[Back in stock] showModal failed', e);
      return;
    }

    document.body.style.overflow = 'hidden';
    if (closeBtn) closeBtn.focus();
    queueSyncShopifyFormsCustomFields(section);
  }

  function getShopifyFormsEmbed(formWrap) {
    return formWrap && formWrap.querySelector('shopify-forms-embed');
  }

  function queryDeepInOpenShadowRoots(entryRoot, selector) {
    if (!entryRoot) return null;
    const queue = [entryRoot];
    const seen = new Set();
    while (queue.length) {
      const root = queue.shift();
      if (!root || seen.has(root)) continue;
      seen.add(root);
      try {
        const hit = root.querySelector(selector);
        if (hit) return hit;
        root.querySelectorAll('*').forEach(function (el) {
          if (el.shadowRoot) queue.push(el.shadowRoot);
        });
      } catch (e) {
        /* closed shadow or security */
      }
    }
    return null;
  }

  function queryShopifyFormsCustomFieldInput(formWrap, fieldKey) {
    const embed = getShopifyFormsEmbed(formWrap);
    if (!embed || !embed.shadowRoot) return null;
    return queryDeepInOpenShadowRoots(
      embed.shadowRoot,
      '[data-testid="field-custom#' + fieldKey + '"]'
    );
  }

  function shopifyFormsFieldLayerReady(formWrap) {
    const embed = getShopifyFormsEmbed(formWrap);
    if (!embed || !embed.shadowRoot) return false;
    return !!(
      queryShopifyFormsCustomFieldInput(formWrap, 'title') ||
      queryShopifyFormsCustomFieldInput(formWrap, 'product') ||
      queryShopifyFormsCustomFieldInput(formWrap, 'color') ||
      queryShopifyFormsCustomFieldInput(formWrap, 'colour') ||
      queryShopifyFormsCustomFieldInput(formWrap, 'size') ||
      queryShopifyFormsCustomFieldInput(formWrap, 'variant') ||
      queryDeepInOpenShadowRoots(embed.shadowRoot, '[data-testid="form"]')
    );
  }

  function hideFieldContainer(input) {
    if (!input) return;
    const row = input.closest('[class*="_formFieldContainer_"]') || input.parentElement;
    if (row) {
      row.setAttribute('hidden', '');
      row.style.setProperty('display', 'none', 'important');
      row.setAttribute('aria-hidden', 'true');
    }
    try {
      input.setAttribute('tabindex', '-1');
      input.setAttribute('aria-hidden', 'true');
    } catch (e) {
      /* ignore */
    }
  }

  function getSelectedColorSwatchValue(section) {
    if (!section) return '';
    const selected = section.querySelector('.flex-color_swatch .width_swatch.select_border');
    if (!selected) return '';
    const link = selected.querySelector('a.link_main');
    if (!link) return '';
    const title = (link.getAttribute('title') || '').trim();
    if (!title) return '';
    // Common theme format: "AQUA MARINE - BEASTIE SS"
    if (title.includes(' - ')) {
      return title.split(' - ')[0].trim();
    }
    return title;
  }

  function pickColorValue(section, variant) {
    const colorFromSwatch = getSelectedColorSwatchValue(section);
    if (colorFromSwatch) return colorFromSwatch;
    if (!variant) return '';
    const c = cfg();
    if (typeof c.colorOptionIndex === 'number' && variant.options && variant.options[c.colorOptionIndex] != null) {
      return String(variant.options[c.colorOptionIndex]);
    }
    if (variant.options && variant.options.length > 1) {
      return String(variant.options[variant.options.length - 1]);
    }
    if (variant.option3) return String(variant.option3);
    if (variant.option2) return String(variant.option2);
    // For single-option products this is typically size, so avoid filling color from option1.
    return '';
  }

  function pickSizeValue(variant) {
    if (!variant) return '';
    const c = cfg();
    if (typeof c.sizeOptionIndex === 'number' && variant.options && variant.options[c.sizeOptionIndex] != null) {
      return String(variant.options[c.sizeOptionIndex]);
    }
    if (variant.options && variant.options.length > 1) {
      return String(variant.options[0]);
    }
    if (variant.option1) return String(variant.option1);
    return '';
  }

  function getVariantsJson(section) {
    const sel = section.querySelector('product-selector');
    const script = sel && sel.querySelector('script[type="application/json"]');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function getCurrentVariant(section) {
    const variants = getVariantsJson(section);
    if (!variants || !variants.length) return null;
    const idInput = section.querySelector('product-form input[name="id"]');
    const vid = idInput ? parseInt(idInput.value, 10) : NaN;
    if (!Number.isFinite(vid)) return variants[0];
    return variants.find(v => v.id === vid) || variants[0];
  }

  function syncShopifyFormsCustomFields(section) {
    const wrap = formWrapInSection(section);
    if (!wrap) return;

    if (!getShopifyFormsEmbed(wrap)?.shadowRoot) return;

    const variant = getCurrentVariant(section);
    const titleEl = section.querySelector('.product__title');
    const productTitle = titleEl ? titleEl.textContent.trim() : '';

    const colorVal = pickColorValue(section, variant);
    const values = {
      title: productTitle || (variant && variant.title) || '',
      product: productTitle || (variant && variant.title) || '',
      color: colorVal,
      colour: colorVal,
      size: pickSizeValue(variant),
      variant: variant ? String(variant.id) : '',
      url: typeof window.location !== 'undefined' ? window.location.href : ''
    };

    CUSTOM_FIELD_KEYS.forEach(function (key) {
      const input = queryShopifyFormsCustomFieldInput(wrap, key);
      if (!input) return;
      if (cfg().hideAutofilledCustomFields === true) {
        hideFieldContainer(input);
      }
      const v = values[key];
      if (v != null && v !== '') {
        input.value = v;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function queueSyncShopifyFormsCustomFields(section) {
    if (!section) return;
    let attempts = 0;
    const max = 40;
    const tick = function () {
      attempts += 1;
      const wrap = formWrapInSection(section);
      if (wrap && shopifyFormsFieldLayerReady(wrap)) {
        syncShopifyFormsCustomFields(section);
        return;
      }
      if (attempts < max) {
        setTimeout(tick, 100);
      }
    };
    tick();
  }

  function setupVariantSyncForSection(section) {
    if (!section || section.hasAttribute('data-forms-variant-sync')) return;
    const wrap = formWrapInSection(section);
    if (!wrap) return;

    section.setAttribute('data-forms-variant-sync', 'true');

    const idInput = section.querySelector('product-form input[name="id"]');
    if (idInput) {
      idInput.addEventListener('change', function () {
        queueSyncShopifyFormsCustomFields(section);
      });
    }

    section.addEventListener('change', function (e) {
      if (e.target.type === 'number') return;
      if (e.target.closest && e.target.closest('product-selector')) {
        queueSyncShopifyFormsCustomFields(section);
      }
    });

    if (idInput) {
      const mo = new MutationObserver(function () {
        queueSyncShopifyFormsCustomFields(section);
      });
      mo.observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  function createTriggerButton(wrapper) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button product-selector__submit ' + TRIGGER_CLASS + ' button--filled';
    btn.textContent = triggerLabel();
    btn.setAttribute('aria-label', triggerLabel());
    btn.addEventListener('click', function () {
      const section = sectionRootFromWrapper(wrapper);
      const wrap = formWrapInSection(section);
      if (wrap) {
        openBackInStockModal(wrap, section);
      } else {
        console.warn(
          '[Back in stock] No Shopify Forms app block in this section (add Forms block to main product, or block id must contain forms_inline).'
        );
      }
    });
    return btn;
  }

  function updateWrapperVisibility(wrapper, addButton) {
    const isDisabled = addButton.hasAttribute('disabled');
    const section = sectionRootFromWrapper(wrapper);
    const formWrap = formWrapInSection(section);
    if (isDisabled) {
      wrapper.classList.add('is-showing-notify');
    } else {
      wrapper.classList.remove('is-showing-notify');
      if (formWrap) {
        hideFormWrap(formWrap);
      }
    }
  }

  function setupWrapper(wrapper) {
    if (wrapper.hasAttribute('data-back-in-stock-inline-setup')) return;
    const addButton = wrapper.querySelector('button[name="add"].no-js-hidden');
    if (!addButton) return;

    wrapper.setAttribute('data-back-in-stock-inline-setup', 'true');

    const section = sectionRootFromWrapper(wrapper);
    if (section) {
      setupVariantSyncForSection(section);
    }

    const triggerBtn = createTriggerButton(wrapper);
    wrapper.appendChild(triggerBtn);

    updateWrapperVisibility(wrapper, addButton);

    const observer = new MutationObserver(function () {
      updateWrapperVisibility(wrapper, addButton);
    });
    observer.observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  function init() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(setupWrapper);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  document.addEventListener('shopify:section:unload', function () {
    const d = document.querySelector('dialog.' + DIALOG_CLASS);
    if (d && d.open) {
      d.close();
    }
  });
})();
